import { useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import SectionEditor from "./SectionEditor";

const CUSTOM_TYPES = [
  { id: "text", label: "Text / Rich Text" },
  { id: "image_text", label: "Image + Text" },
  { id: "video", label: "Video" },
  { id: "banner", label: "Promotional Banner" },
  { id: "cta", label: "Call To Action" },
  { id: "gallery", label: "Gallery" },
];

const TYPE_LABELS = {
  hero: "Hero / Banner", marquee: "Marquee", categories: "Product Categories",
  products: "Product Showcase", manifesto: "Features / Why Us", occasions: "Occasions + CTA",
  testimonials: "Testimonials", instagram: "Social / Instagram", whatsapp_cta: "Contact / WhatsApp",
  text: "Text", image_text: "Image + Text", video: "Video", banner: "Banner", cta: "CTA", gallery: "Gallery",
};

export default function SectionsManager({ sections, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newType, setNewType] = useState("text");

  const patch = async (id, payload) => {
    try {
      await api.patch(`/admin/sections/${id}`, payload);
      onChanged();
    } catch {
      toast.error("Could not update the section.");
    }
  };

  const move = async (index, dir) => {
    const next = [...sections];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    try {
      await api.post("/admin/sections", { ordered_ids: next.map((s) => s.id) });
      onChanged();
    } catch {
      toast.error("Could not reorder sections.");
    }
  };

  const add = async () => {
    try {
      await api.post("/admin/sections", { type: newType, name: TYPE_LABELS[newType] });
      setAdding(false);
      onChanged();
      toast.success("Section added — click Edit to fill in its content.");
    } catch {
      toast.error("Could not add the section.");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete the "${s.name}" section from the homepage?`)) return;
    try {
      await api.delete(`/admin/sections/${s.id}`);
      onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error((Array.isArray(detail) ? detail[0]?.msg : detail) || "This section cannot be deleted.");
    }
  };

  return (
    <div data-testid="sections-manager">
      <div className="mb-5 flex items-center justify-between gap-3">
        <p className="text-sm text-[#5C5C5C]">
          Edit, hide, reorder or add homepage sections. Changes go live immediately.
        </p>
        <button
          data-testid="add-section-button"
          onClick={() => setAdding(true)}
          className="inline-flex shrink-0 items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-medium text-cream transition-colors hover:bg-rose hover:text-white"
        >
          <Plus size={15} /> Add Section
        </button>
      </div>

      {adding && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl border border-rose/30 bg-rose/5 p-4">
          <select
            data-testid="add-section-type"
            value={newType}
            onChange={(e) => setNewType(e.target.value)}
            className="rounded-xl border border-line bg-white px-4 py-2.5 text-sm outline-none"
          >
            {CUSTOM_TYPES.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <button data-testid="add-section-confirm" onClick={add} className="rounded-full bg-ink px-5 py-2.5 text-xs font-medium text-cream hover:bg-rose hover:text-white">
            Add
          </button>
          <button onClick={() => setAdding(false)} className="rounded-full border border-line px-5 py-2.5 text-xs font-medium">
            Cancel
          </button>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {sections.map((s, i) => (
          <div
            key={s.id}
            data-testid={`section-row-${s.id}`}
            className={`flex flex-wrap items-center gap-3 rounded-2xl border border-line bg-white p-4 ${!s.visible ? "opacity-60" : ""}`}
          >
            <div className="flex flex-col">
              <button data-testid={`section-up-${s.id}`} onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1 text-[#5C5C5C] transition-colors hover:text-rose disabled:opacity-30">
                <ArrowUp size={14} />
              </button>
              <button data-testid={`section-down-${s.id}`} onClick={() => move(i, 1)} disabled={i === sections.length - 1} aria-label="Move down" className="p-1 text-[#5C5C5C] transition-colors hover:text-rose disabled:opacity-30">
                <ArrowDown size={14} />
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg font-medium">{s.name}</p>
              <p className="text-[11px] uppercase tracking-wider text-[#5C5C5C]">{TYPE_LABELS[s.type] || s.type}</p>
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
              <input
                data-testid={`section-visible-${s.id}`}
                type="checkbox"
                checked={s.visible}
                onChange={(e) => patch(s.id, { visible: e.target.checked })}
                className="h-4 w-4 accent-[#C48B81]"
              />
              Visible
            </label>
            <button
              data-testid={`section-edit-${s.id}`}
              onClick={() => setEditing(s)}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink px-4 py-2 text-xs font-medium transition-colors hover:bg-ink hover:text-cream"
            >
              <Pencil size={12} /> Edit
            </button>
            {s.is_custom ? (
              <button
                data-testid={`section-delete-${s.id}`}
                onClick={() => remove(s)}
                aria-label={`Delete ${s.name}`}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-[#5C5C5C] transition-colors hover:border-red-400 hover:text-red-500"
              >
                <Trash2 size={14} />
              </button>
            ) : (
              <span className="w-9" title="Built-in section — hide instead of deleting" />
            )}
          </div>
        ))}
      </div>

      {editing && (
        <SectionEditor
          section={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}
